import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { usePrefs } from './prefs';
import { Mascot } from './Mascot';
import { ART } from './art';

/** Same lines as the instant loading screen in index.html / public/boot.js. */
const TIPS = ['Blowing up the balloons…', 'Shuffling the grudges…', 'Warming up the buzzers…', 'Hiding the secret objectives…', 'Polishing the trophies…', 'Teaching the AI players to trash-talk…'];

/**
 * A game's cover artwork at its own shape (never stretched or cropped), capped in height: the top of the
 * game-loading and final-results screens. Null when the game has no artwork yet.
 */
export function GameArtBanner({ gameId, big }: { gameId?: string | null; big?: boolean }) {
  const cover = gameId ? ART[gameId]?.cover : undefined;
  if (!cover) return null;
  return (
    <img src={cover} alt="" aria-hidden data-testid="game-art-banner"
      className="block mx-auto w-auto h-auto max-w-full rounded-3xl shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
      style={{ maxHeight: big ? 300 : 170 }} />
  );
}

/**
 * Loading screen used inside the app (a game loading, reconnecting, the TV connecting): Pop bouncing (or, for
 * a game, its artwork), a moving bar and a rotating line. Pass `label` to say what is happening; it replaces the jokes.
 */
export function LoadingScreen({ label, big, gameId, children }: { label?: string; big?: boolean; gameId?: string | null; children?: ReactNode }) {
  const { reducedMotion } = usePrefs();
  const [tip, setTip] = useState(0);
  useEffect(() => {
    if (label) return;
    const t = setInterval(() => setTip((i) => (i + 1) % TIPS.length), 1800);
    return () => clearInterval(t);
  }, [label]);
  return (
    <div className="flex-1 grid place-items-center text-center p-6" role="status" aria-live="polite" data-testid="loading-screen">
      <div className="flex flex-col items-center gap-4">
        {gameId && ART[gameId]?.cover ? <GameArtBanner gameId={gameId} big={big} /> : <Mascot size={big ? 260 : 170} />}
        <div className={`rounded-full bg-white/10 overflow-hidden ${big ? 'w-96 h-4' : 'w-64 h-3'}`} aria-hidden>
          <motion.i
            className="block h-full w-2/5 rounded-full bg-gradient-to-r from-[#ff3d7f] via-[#ffd23f] to-[#00e5ff]"
            animate={reducedMotion ? undefined : { x: ['-110%', '260%'] }}
            transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
        <p className={`text-mute min-h-[2.6em] max-w-[30ch] ${big ? 'text-2xl' : 'text-base'}`}>{label ?? TIPS[tip]}</p>
        {children}
      </div>
    </div>
  );
}
